import { motion } from "framer-motion";
import { BookOpen, MessageCircle, Feather, Star } from "lucide-react";
import { ChapterHeading, FadeUp, WHATSAPP_URL, FloralCorner } from "./shared";

const TAKEAWAYS = [
    "Reflexões práticas sobre o cuidado integral à pessoa idosa",
    "Orientações afetuosas para famílias e cuidadores",
    "Saúde mental, dignidade e bem-estar no envelhecer",
];

const BookSection = () => (
    <section id="livro" className="relative overflow-hidden bg-plum py-20 sm:py-28" data-testid="book-section">
        <div className="pointer-events-none absolute -left-40 top-0 h-[30rem] w-[30rem] rounded-full bg-lilac/20 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -right-40 bottom-0 h-[30rem] w-[30rem] rounded-full bg-serenity/20 blur-3xl" aria-hidden="true" />
        <FloralCorner className="pointer-events-none absolute right-0 top-0 h-56 w-56 rotate-90 text-lilac/25" />

        <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-2">
            <FadeUp className="relative mx-auto w-full max-w-sm" style={{ perspective: 1200 }}>
                <motion.div
                    whileHover={{ rotateY: -10, rotateX: 4, scale: 1.02 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    style={{ transformStyle: "preserve-3d" }}
                    className="relative"
                >
                    <div className="absolute inset-0 translate-y-6 scale-95 rounded-2xl bg-black/30 blur-2xl" aria-hidden="true" />
                    <img
                        src="/assets/book.png"
                        alt="Livro Cuidados que Transformam, de Maricleia Araújo"
                        className="relative w-full rounded-2xl shadow-2xl"
                        data-testid="book-mockup"
                    />
                </motion.div>
                <div className="absolute -left-4 -top-4 flex items-center gap-2 rounded-full bg-gradient-to-r from-[#F3E5AB] via-[#D4AF37] to-[#C59B27] px-5 py-2.5 text-xs font-bold uppercase tracking-[0.18em] text-white shadow-lg shadow-gold/40 sm:-left-8">
                    <Star size={14} fill="currentColor" />
                    Obra da Autora
                </div>
            </FadeUp>

            <div>
                <ChapterHeading dark number="03" eyebrow="O Livro" title={<>Conhecimento que <span className="italic text-serenitylight">transforma</span> o cuidar</>} />
                <FadeUp delay={0.15}>
                    <p className="mt-3 flex items-center gap-3 font-serif text-2xl italic text-lav sm:text-3xl">
                        <BookOpen size={26} className="shrink-0 text-gold" />
                        Cuidados que Transformam
                    </p>
                    <p className="mt-6 text-base leading-relaxed text-lav/90 sm:text-lg">
                        Nesta obra, reúno minha experiência em gerontologia em um convite sensível e profundo: enxergar o cuidado como um ato de amor, presença e transformação — para quem cuida e para quem é cuidado.
                    </p>
                </FadeUp>
                <ul className="mt-8 space-y-4">
                    {TAKEAWAYS.map((t, i) => (
                        <FadeUp key={t} delay={0.2 + i * 0.12} className="flex items-start gap-3.5">
                            <Feather size={18} className="mt-1 shrink-0 text-gold" />
                            <li className="list-none text-base text-cream sm:text-lg">{t}</li>
                        </FadeUp>
                    ))}
                </ul>
                <FadeUp delay={0.5}>
                    <a
                        href={`${WHATSAPP_URL}?text=${encodeURIComponent("Olá, Maricleia! Gostaria de saber mais sobre o livro Cuidados que Transformam.")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="book-cta-button"
                        className="mt-10 inline-flex items-center gap-2.5 rounded-full bg-gradient-to-r from-lilac via-serenity to-serenitylight px-8 py-4 text-base font-semibold text-white shadow-xl shadow-black/20 transition-transform duration-300 hover:scale-[1.03]"
                    >
                        <MessageCircle size={19} />
                        Saiba mais sobre o livro
                    </a>
                </FadeUp>
            </div>
        </div>
    </section>
);

export default BookSection;
